import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { X, Sparkles, ArrowRight, Clock } from "lucide-react";
import { useBilling } from "@/hooks/useBilling";

/**
 * Welcome popup shown once per shop on the first load after install while the
 * 24h free-generation trial is active. Dismissal is persisted per shop in
 * localStorage so returning merchants aren't nagged.
 */
export function WelcomeTrialModal() {
  const { plan, trial, loading } = useBilling();
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState(true);

  const storageKey = plan?.shop_id ? `ar-welcome-seen:${plan.shop_id}` : null;

  useEffect(() => {
    if (loading || !storageKey) return;
    try {
      setDismissed(localStorage.getItem(storageKey) === "1");
    } catch {
      setDismissed(false);
    }
  }, [loading, storageKey]);

  const close = () => {
    setDismissed(true);
    if (storageKey) {
      try {
        localStorage.setItem(storageKey, "1");
      } catch {
        // localStorage unavailable (e.g. blocked third-party storage) — modal
        // will simply reappear next load, which is acceptable.
      }
    }
  };

  if (loading || dismissed || !trial?.active || trial.used) {
    return null;
  }

  const hoursLeft = trial.expiresAt
    ? Math.max(0, Math.floor((new Date(trial.expiresAt).getTime() - Date.now()) / (1000 * 60 * 60)))
    : 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={close} />
      <div className="relative w-full max-w-md rounded-3xl border border-white/80 bg-white p-6 shadow-2xl">
        <button
          onClick={close}
          aria-label="Close welcome message"
          className="absolute right-4 top-4 flex h-8 w-8 items-center justify-center rounded-full bg-slate-100 text-slate-500 transition hover:bg-slate-200"
        >
          <X className="h-4 w-4" />
        </button>

        <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-r from-indigo-500 to-sky-500 shadow-lg shadow-indigo-200">
          <Sparkles className="h-6 w-6 text-white" />
        </div>

        <h2 className="text-xl font-semibold text-slate-900">
          Welcome! Your first AR product is free
        </h2>
        <p className="mt-2 text-sm text-slate-600">
          Generate your first 3D model now — pick any product, hit Generate, and
          see it live in AR on your storefront. No plan required, and no time
          limit: the free product is yours to keep.
        </p>

        {/* Only the PREMIUM modes are time-boxed; the free product itself never
            expires. Framed as a bonus that is running out, not as the product
            being taken away. */}
        <div className="mt-4 flex items-center gap-2 rounded-2xl border border-indigo-100 bg-indigo-50/60 px-4 py-2.5">
          <Clock className="h-4 w-4 text-indigo-600" />
          <span className="text-sm font-medium text-indigo-700">
            Premium capture &amp; render free on it for about {hoursLeft}h more
          </span>
        </div>

        <div className="mt-5 flex flex-col gap-2">
          <button
            onClick={() => {
              close();
              navigate("/products");
            }}
            className="flex w-full items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-indigo-500 to-sky-500 px-6 py-3 text-sm font-semibold text-white shadow-lg shadow-indigo-200 transition hover:shadow-xl"
          >
            Generate my first 3D model
            <ArrowRight className="h-4 w-4" />
          </button>
          <button
            onClick={close}
            className="w-full rounded-2xl border border-slate-200 bg-white px-6 py-2.5 text-sm font-medium text-slate-600 transition hover:bg-slate-50"
          >
            Explore the dashboard first
          </button>
        </div>
      </div>
    </div>
  );
}
